/**
 * A reorderable list (dnd-kit). Drag a row by its handle, or focus the handle and press Space,
 * move with ↑/↓, Space to drop and Esc to cancel. Screen readers hear every step through the
 * caller's translated announcements.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { IconButton } from './button';

export interface SortableListLabels {
  /** Accessible name of each row's drag handle, e.g. "Move ^GSPC". */
  handle: (id: string) => string;
  /** How to reorder with the keyboard, read once when a handle gets focus. */
  instructions: string;
  pickedUp: (id: string, position: number, total: number) => string;
  movedTo: (id: string, position: number, total: number) => string;
  dropped: (id: string, position: number, total: number) => string;
  cancelled: (id: string) => string;
}

export interface SortableListProps<T> {
  /** Accessible name of the list. */
  label: string;
  items: readonly T[];
  getId: (item: T) => string;
  onReorder: (items: T[]) => void;
  renderItem: (item: T) => ReactNode;
  labels: SortableListLabels;
  /** True while a row is being moved, so a surrounding dialog can leave Esc to the list. */
  onDraggingChange?: (dragging: boolean) => void;
}

export function SortableList<T>({
  label,
  items,
  getId,
  onReorder,
  renderItem,
  labels,
  onDraggingChange,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);
  const position = (id: string | number): number => ids.indexOf(String(id)) + 1;
  const moved = useRef(false);
  const announcements: Announcements = {
    onDragStart: ({ active }) => {
      moved.current = false;
      return labels.pickedUp(String(active.id), position(active.id), ids.length);
    },
    onDragOver: ({ active, over }) => {
      // Right after pickup the row is "over" itself; keep the pickup announcement.
      if (!over || (!moved.current && over.id === active.id)) return undefined;
      moved.current = true;
      return labels.movedTo(String(active.id), position(over.id), ids.length);
    },
    onDragEnd: ({ active, over }) =>
      labels.dropped(String(active.id), position(over?.id ?? active.id), ids.length),
    onDragCancel: ({ active }) => labels.cancelled(String(active.id)),
  };

  function onDragEnd({ active, over }: DragEndEvent): void {
    onDraggingChange?.(false);
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove([...items], from, to));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: labels.instructions },
      }}
      onDragStart={() => onDraggingChange?.(true)}
      onDragCancel={() => onDraggingChange?.(false)}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ul
          aria-label={label}
          className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-bg-1"
        >
          {items.map((item) => (
            <Row key={getId(item)} id={getId(item)} handleLabel={labels.handle(getId(item))}>
              {renderItem(item)}
            </Row>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function Row({
  id,
  handleLabel,
  children,
}: {
  id: string;
  handleLabel: string;
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'relative flex h-9 items-center gap-2 bg-bg-1 pr-2 pl-1',
        isDragging && 'z-10 bg-bg-4',
      )}
    >
      <IconButton
        ref={setActivatorNodeRef}
        size="sm"
        label={handleLabel}
        className="cursor-grab touch-none active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </IconButton>
      {children}
    </li>
  );
}
